import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField as MuiTextField, Select, MenuItem, FormControl, InputAdornment, IconButton } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { parseISO, isValid, format, parse } from 'date-fns';
import { Iconify } from 'src/components';
import { formRowSx, formGroupSx, formLabelSx, formInputSx } from '../styles';
import SpeechToTextButton from './SpeechToTextButton';

// Form Row - Container for form fields
export const FormRow = ({ children, sx = {} }) => (
  <Box sx={{ ...formRowSx, ...sx }}>{children}</Box>
);

FormRow.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

// Form Group - Single field container
export const FormGroup = ({ children, width = 'full', sx = {} }) => (
  <Box sx={{ ...formGroupSx(width), ...sx }}>{children}</Box>
);

FormGroup.propTypes = {
  children: PropTypes.node,
  width: PropTypes.oneOf(['full', 'narrow', 'medium']),
  sx: PropTypes.object,
};

// Form Label
export const FormLabel = ({ children, required = false }) => (
  <Typography component="label" sx={formLabelSx}>
    {children}
    {required && <span className="req"> *</span>}
  </Typography>
);

FormLabel.propTypes = {
  children: PropTypes.node,
  required: PropTypes.bool,
};

// Text Input Field
export const ScriptTextField = ({
  label,
  required = false,
  value,
  onChange,
  placeholder,
  type = 'text',
  width = 'full',
  isFilled = false,
  ...props
}) => {
  const handleLiveUpdate = useCallback((fullText) => {
    if (onChange) {
      onChange({ target: { value: fullText } });
    }
  }, [onChange]);

  const showMic = type !== 'number' && type !== 'date';

  return (
    <FormGroup width={width}>
      {label && <FormLabel required={required}>{label}</FormLabel>}
      <MuiTextField
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={type}
        fullWidth
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            ...formInputSx(isFilled || !!value, required),
            height: '40px',
          },
          '& .MuiInputBase-input': {
            padding: '10px 14px',
          },
        }}
        InputProps={{
          endAdornment: showMic ? (
            <InputAdornment position="end" sx={{ mr: -0.5 }}>
              <SpeechToTextButton
                currentValue={value || ''}
                onLiveUpdate={handleLiveUpdate}
              />
            </InputAdornment>
          ) : undefined,
        }}
        {...props}
      />
    </FormGroup>
  );
};

ScriptTextField.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  width: PropTypes.string,
  isFilled: PropTypes.bool,
};

// Helper to get select background color
const getSelectBgColor = (hasValue, showRequired) => {
  if (hasValue) return 'rgba(0,184,217,0.06)';
  if (showRequired) return 'rgba(255,86,48,0.02)';
  return '#fff';
};

// Helper to get select border color
const getSelectBorderColor = (hasValue, showRequired) => {
  if (hasValue) return '#00B8D9';
  if (showRequired) return '#FFAC82';
  return '#E0E0E0';
};

// Select Field
export const ScriptSelect = ({
  label,
  required = false,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  width = 'full',
  isFilled = false,
  menuMaxHeight,
  ...props
}) => {
  const hasValue = isFilled || !!value;
  const showRequired = required && !hasValue;

  return (
    <FormGroup width={width}>
      {label && <FormLabel required={required}>{label}</FormLabel>}
      <FormControl fullWidth size="small">
        <Select
          value={value || ''}
          onChange={onChange}
          displayEmpty
          sx={{
            bgcolor: getSelectBgColor(hasValue, showRequired),
            borderRadius: '8px',
            height: '40px',
            cursor: 'pointer',
            '& .MuiOutlinedInput-notchedOutline': {
              border: `1.5px solid ${getSelectBorderColor(hasValue, showRequired)}`,
              borderRadius: '8px',
              borderLeft: showRequired ? '3px solid #FF5630' : undefined,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: hasValue ? '#00B8D9' : '#919EAB',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: '1.5px solid #00A76F',
              boxShadow: '0 0 0 3px rgba(0,167,111,0.12)',
            },
            '& .MuiSelect-select': {
              padding: '10px 14px',
              fontSize: '0.875rem',
              fontWeight: hasValue ? 500 : 400,
              color: hasValue ? '#006C9C' : '#212B36',
              minHeight: 'unset',
              lineHeight: 1.5,
            },
            '& .MuiSelect-icon': {
              color: '#637381',
              right: '12px',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 0.5,
                borderRadius: '8px',
                ...(menuMaxHeight && { maxHeight: menuMaxHeight }),
                boxShadow: '0 0 2px 0 rgba(145,158,171,0.2), 0 12px 24px -4px rgba(145,158,171,0.12)',
                '& .MuiMenuItem-root': {
                  fontSize: '0.875rem',
                  padding: '10px 14px',
                  '&:hover': {
                    bgcolor: 'rgba(145,158,171,0.08)',
                  },
                  '&.Mui-selected': {
                    bgcolor: 'rgba(0,167,111,0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(0,167,111,0.12)',
                    },
                  },
                },
              },
            },
          }}
          {...props}
        >
          <MenuItem value="" disabled sx={{ color: '#919EAB' }}>
            {placeholder}
          </MenuItem>
          {options.map((option) => (
            <MenuItem
              key={typeof option === 'string' ? option : option.value}
              value={typeof option === 'string' ? option : option.value}
            >
              {typeof option === 'string' ? option : option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </FormGroup>
  );
};

ScriptSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      }),
    ])
  ),
  placeholder: PropTypes.string,
  width: PropTypes.string,
  isFilled: PropTypes.bool,
  menuMaxHeight: PropTypes.number,
};

// Textarea Field
export const ScriptTextarea = ({
  label,
  required = false,
  value,
  onChange,
  placeholder,
  rows = 3,
  width = 'full',
  isFilled = false,
  ...props
}) => {
  const handleLiveUpdate = useCallback((fullText) => {
    if (onChange) {
      onChange({ target: { value: fullText } });
    }
  }, [onChange]);

  return (
    <FormGroup width={width}>
      {label && <FormLabel required={required}>{label}</FormLabel>}
      <Box sx={{ position: 'relative' }}>
        <MuiTextField
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          multiline
          rows={rows}
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': formInputSx(isFilled || !!value, required),
            '& .MuiInputBase-input': {
              padding: '12px 14px',
              paddingBottom: '28px',
            },
          }}
          {...props}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 10,
          }}
        >
          <SpeechToTextButton
            currentValue={value || ''}
            onLiveUpdate={handleLiveUpdate}
          />
        </Box>
      </Box>
    </FormGroup>
  );
};

ScriptTextarea.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  width: PropTypes.string,
  isFilled: PropTypes.bool,
};

// Helper to get date background color
const getDateBgColor = (hasValue, showRequired) => {
  if (hasValue) return 'rgba(0,184,217,0.06)';
  if (showRequired) return 'rgba(255,86,48,0.02)';
  return '#fff';
};

// Helper to get date border color
const getDateBorderColor = (hasValue, showRequired) => {
  if (hasValue) return '#00B8D9';
  if (showRequired) return '#FFAC82';
  return '#E0E0E0';
};

// Helper to get date input styles
const getDateInputStyles = (hasValue, showRequired) => ({
  bgcolor: getDateBgColor(hasValue, showRequired),
  borderRadius: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: `1.5px solid ${getDateBorderColor(hasValue, showRequired)}`,
    borderRadius: '8px',
    borderLeft: showRequired ? '3px solid #FF5630' : undefined,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: hasValue ? '#00B8D9' : '#919EAB',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '1.5px solid #00A76F',
    boxShadow: '0 0 0 3px rgba(0,167,111,0.12)',
  },
  '& .MuiInputBase-input': {
    padding: '11px 14px',
    fontSize: '0.875rem',
    fontWeight: hasValue ? 500 : 400,
    color: hasValue ? '#006C9C' : '#212B36',
  },
});

// Date Picker Field
export const ScriptDateField = ({
  label,
  required = false,
  value,
  onChange,
  width = 'full',
  isFilled = false,
  maxDate,
  minDate,
  ...props
}) => {
  // Convert string value to Date for the picker - ensure we only use valid dates
  // Check for empty/null/undefined values first
  const isEmptyValue = value === null || value === undefined || value === '' || value === 'null';
  let dateValue = null;

  if (!isEmptyValue) {
    // Try parsing ISO format first (YYYY-MM-DD)
    let parsedDate = parseISO(value);
    if (!isValid(parsedDate) || parsedDate.getFullYear() <= 1900) {
      // Try parsing DD-MM-YYYY format (common API format)
      parsedDate = parse(value, 'dd-MM-yyyy', new Date());
    }
    if (!isValid(parsedDate) || parsedDate.getFullYear() <= 1900) {
      // Try parsing DD/MM/YYYY format
      parsedDate = parse(value, 'dd/MM/yyyy', new Date());
    }
    // Only use the parsed date if it's valid and represents a real date
    if (isValid(parsedDate) && parsedDate.getFullYear() > 1900) {
      dateValue = parsedDate;
    }
  }

  const hasValue = isFilled || (dateValue !== null);
  const showRequired = required && !hasValue;

  // Handle date change and convert back to string format
  const handleDateChange = (newValue) => {
    if (onChange) {
      let formattedValue = '';
      if (newValue && isValid(newValue)) {
        // Format as YYYY-MM-DD string for API
        formattedValue = format(newValue, 'yyyy-MM-dd');
      }
      // Create a synthetic event-like object to match TextField onChange signature
      onChange({ target: { value: formattedValue } });
    }
  };

  // Convert maxDate/minDate to Date objects if they are strings
  const parseDateProp = (dateProp) => {
    if (!dateProp) return undefined;
    if (dateProp instanceof Date) return dateProp;
    const parsed = parseISO(dateProp);
    return isValid(parsed) ? parsed : undefined;
  };

  return (
    <FormGroup width={width}>
      {label && <FormLabel required={required}>{label}</FormLabel>}
      <MobileDatePicker
        value={dateValue}
        onChange={handleDateChange}
        format="dd/MM/yyyy"
        maxDate={parseDateProp(maxDate)}
        minDate={parseDateProp(minDate)}
        slotProps={{
          textField: {
            fullWidth: true,
            size: 'small',
            placeholder: 'DD/MM/YYYY',
            sx: getDateInputStyles(hasValue, showRequired),
            InputProps: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end">
                    <Iconify icon="solar:calendar-mark-bold-duotone" sx={{ width: 20, height: 20, color: '#637381' }} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          },
        }}
        {...props}
      />
    </FormGroup>
  );
};

ScriptDateField.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  width: PropTypes.string,
  isFilled: PropTypes.bool,
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

export default {
  FormRow,
  FormGroup,
  FormLabel,
  ScriptTextField,
  ScriptSelect,
  ScriptTextarea,
  ScriptDateField,
};
